import { useEffect, useState } from 'react';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

const STAGES = [
  { id: 'listing', label: 'Listing' },
  { id: 'offer', label: 'Offer' },
  { id: 'under_contract', label: 'Under Contract' },
  { id: 'inspection', label: 'Inspection' },
  { id: 'closing', label: 'Closing' },
  { id: 'closed', label: 'Closed' },
];

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

export default function DealDesk() {
  const [txs, setTxs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ property_address: '', client_name: '', price: '', commission: '', stage: 'listing', close_date: '', notes: '' });

  const load = async () => {
    try {
      const { data } = await http.get('/transactions');
      setTxs(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price), commission: form.commission ? parseFloat(form.commission) : null };
      const { data } = await http.post('/transactions', payload);
      setTxs([data, ...txs]);
      setForm({ property_address: '', client_name: '', price: '', commission: '', stage: 'listing', close_date: '', notes: '' });
      setShowForm(false);
      toast.success('Deal added to Desk.');
    } catch (e) { toast.error(formatError(e)); }
  };

  const move = async (t, stage) => {
    try {
      const { data } = await http.patch(`/transactions/${t.id}`, { stage });
      setTxs(txs.map(x => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await http.delete(`/transactions/${id}`); setTxs(txs.filter(x => x.id !== id)); }
    catch (e) { toast.error(formatError(e)); }
  };

  const grouped = STAGES.map(s => ({ ...s, items: txs.filter(t => t.stage === s.id) }));

  const totalActive = txs.filter(t => t.stage !== 'closed').reduce((a, t) => a + (t.commission || 0), 0);
  const totalClosed = txs.filter(t => t.stage === 'closed').reduce((a, t) => a + (t.commission || 0), 0);

  return (
    <div className="space-y-8">
      <header className="hq-fade-in">
        <p className="hq-eyebrow">Deal Desk</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="dealdesk-title">Transaction Pipeline</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Every deal, one panel. Move it forward with a click — HomiHQ tracks the rest.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-4">
        <div className="hq-card p-5">
          <div className="hq-eyebrow">Active Deals</div>
          <div className="font-serif text-3xl mt-2" style={{ color: 'var(--sage)' }} data-testid="active-deals-count">
            {txs.filter(t => t.stage !== 'closed').length}
          </div>
        </div>
        <div className="hq-card p-5">
          <div className="hq-eyebrow">Pending GCI</div>
          <div className="font-serif text-3xl mt-2" style={{ color: 'var(--sage)' }}>{money(totalActive)}</div>
        </div>
        <div className="hq-card p-5">
          <div className="hq-eyebrow">Closed GCI</div>
          <div className="font-serif text-3xl mt-2" style={{ color: 'var(--gold-soft)' }}>{money(totalClosed)}</div>
        </div>
      </div>

      <div className="flex justify-end">
        <button className="hq-btn-primary" onClick={() => setShowForm(!showForm)} data-testid="new-deal-btn">
          {showForm ? 'Cancel' : '+ Add Deal'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="hq-card p-8 grid grid-cols-1 md:grid-cols-2 gap-5 hq-fade-in">
          <div className="md:col-span-2"><label className="hq-label">Property Address</label><input className="hq-input" required data-testid="tx-address-input" value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})} /></div>
          <div><label className="hq-label">Client Name</label><input className="hq-input" value={form.client_name} onChange={e => setForm({...form, client_name: e.target.value})} /></div>
          <div><label className="hq-label">Stage</label>
            <select className="hq-input" value={form.stage} onChange={e => setForm({...form, stage: e.target.value})}>
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div><label className="hq-label">Sale Price (USD)</label><input className="hq-input" type="number" required data-testid="tx-price-input" value={form.price} onChange={e => setForm({...form, price: e.target.value})} /></div>
          <div><label className="hq-label">Commission (USD)</label><input className="hq-input" type="number" value={form.commission} onChange={e => setForm({...form, commission: e.target.value})} /></div>
          <div><label className="hq-label">Close Date</label><input className="hq-input" type="date" value={form.close_date} onChange={e => setForm({...form, close_date: e.target.value})} /></div>
          <div><label className="hq-label">Notes</label><input className="hq-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          <div className="md:col-span-2 text-right">
            <button className="hq-btn-primary" data-testid="save-deal-btn">Save Deal</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {grouped.map(col => (
          <div key={col.id} className="hq-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="hq-eyebrow">{col.label}</div>
                <div className="font-serif text-2xl" style={{ color: 'var(--sage)' }}>{col.items.length}</div>
              </div>
              <div className="hq-dot"></div>
            </div>
            <div className="space-y-3" data-testid={`stage-${col.id}`}>
              {col.items.length === 0 && (
                <div className="text-xs italic py-4 text-center" style={{ color: 'var(--muted)' }}>Empty</div>
              )}
              {col.items.map(t => (
                <div key={t.id} className="p-4" style={{ background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 2 }}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-sm" style={{ color: 'var(--ink)' }}>{t.property_address}</div>
                    <button onClick={() => remove(t.id)}><Trash2 size={12} style={{ color: 'var(--muted)' }} /></button>
                  </div>
                  <div className="text-xs" style={{ color: 'var(--muted)' }}>{t.client_name || '—'}</div>
                  <div className="font-serif text-lg mt-1" style={{ color: 'var(--gold-soft)' }}>{money(t.price)}</div>
                  {t.commission > 0 && <div className="text-xs" style={{ color: 'var(--muted)' }}>GCI: {money(t.commission)}</div>}
                  <select
                    className="mt-3 w-full text-xs px-2 py-1"
                    value={t.stage}
                    onChange={e => move(t, e.target.value)}
                    data-testid={`stage-select-${t.id}`}
                    style={{ background: 'var(--parchment)', border: '1px solid var(--line)', borderRadius: 2 }}
                  >
                    {STAGES.map(s => <option key={s.id} value={s.id}>Move to: {s.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
